import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import FinancialService from '../../../../../AppSettings/services/FinancialService';
import VenExpService from '../../../../../AppSettings/services/VenExpService';
import OppExpService from '../../../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../../../AppSettings/services/GenExpensesService';
import { normalizeToArray } from '../../../../../helpers/responseHelpers';
import { computeBilledTimeAmount } from '../../../../../helpers/billingHelpers';
import FixBillService from '../../../../../AppSettings/services/FixBillService';
import SectionCard from '../../../../../Components/SectionCard/SectionCard';
import './ForecastActualCard.css';

const ForecastActualCard: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [billableTimeTotal, setBillableTimeTotal] = useState<number>(0);
  const [fixedFeesTotal, setFixedFeesTotal] = useState<number>(0);
  const [otherTotal, setOtherTotal] = useState<number>(0);
  const [vendorTotal, setVendorTotal] = useState<number>(0);
  const [operatingTotal, setOperatingTotal] = useState<number>(0);
  const [generalTotal, setGeneralTotal] = useState<number>(0);
  const [fixedBillsTotal, setFixedBillsTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!matterId) return;
      
      try {
        setIsLoading(true);
        setError(null);

        // Fetch billing data using matter-specific endpoints and normalize
        const billedTimeData = await FinancialService.getBilledTime(matterId);
        const billedEntries = normalizeToArray(billedTimeData);
        const billableTimeSum = billedEntries.reduce((sum: number, item: any) =>
          sum + computeBilledTimeAmount(item), 0
        );

        // Fetch fixed fees data and normalize
        const fixedFeesData = await FinancialService.getAssignedFixedFees(matterId);
        const fixedFeesList = normalizeToArray(fixedFeesData as any);
        const fixedFeesSum = fixedFeesList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.fees))) ? parseFloat(String(item.fees)) : 0), 0
        );

        const otherBillingData = await FinancialService.getOtherBillingByMatter(matterId);
        const otherList = normalizeToArray(otherBillingData);
        const otherSum = otherList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        // Fetch expenses data using matter-specific endpoints
        const vendorData = await VenExpService.getVenExpensesByMatter(matterId);
        const vendorList = normalizeToArray(vendorData);
        const vendorSum = vendorList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const operatingData = await OppExpService.getOppExpensesByMatter(matterId);
        const operatingList = normalizeToArray(operatingData);
        const operatingSum = operatingList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const generalData = await GenExpensesService.getGenExpensesByMatter(matterId);
        const generalList = normalizeToArray(generalData);
        const generalSum = generalList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const fixedBillsData = await FixBillService.getFixBillExpensesByMatter(matterId);
        const fixedBillsList = normalizeToArray(fixedBillsData);
        const fixedBillsSum = fixedBillsList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        // Set all the totals
        setBillableTimeTotal(billableTimeSum);
        setFixedFeesTotal(fixedFeesSum);
        setOtherTotal(otherSum);
        setVendorTotal(vendorSum);
        setOperatingTotal(operatingSum);
        setGeneralTotal(generalSum);
        setFixedBillsTotal(fixedBillsSum);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to fetch data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (matterId) {
      fetchData();
    }
  }, [matterId]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate Forecast values
  const forecastProfits = billableTimeTotal + fixedFeesTotal + otherTotal;
  const forecastCosts = vendorTotal + operatingTotal + generalTotal + fixedBillsTotal;
  const forecastTotal = forecastProfits + forecastCosts;

  // For now, Actual uses the same data as Forecast
  // TODO: Update this to fetch actual/completed transactions when actual data source is defined
  const actualProfits = 0;
  const actualCosts = 0;
  const actualTotal = actualProfits + actualCosts;

  // Icon component for top right
  const iconElement = (
    <div className="forecastActualCard-icon-container">
      <FontAwesomeIcon icon={faChartLine} className="forecastActualCard-icon" />
    </div>
  );

  if (isLoading) {
    return (
      <SectionCard title="Forecast vs Actual" right={iconElement}>
        <div>Loading...</div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Forecast vs Actual" right={iconElement}>
        <div>Error: {error}</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard 
      title="Forecast vs Actual" 
      right={iconElement}
      className="forecastActualCard-container"
    >
      <div className="forecastActualCard-content">
        {/* Forecast Section */}
        <div className="forecastActualCard-section">
          <div className="forecastActualCard-section-title">Forecast</div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Total</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastTotal)}</span>
          </div>
          
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Profits</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastProfits)}</span>
          </div>
          
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Costs</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastCosts)}</span>
          </div>
        </div>

        <div className="forecastActualCard-divider"></div>

        {/* Actual Section */}
        <div className="forecastActualCard-section">
          <div className="forecastActualCard-section-title">Actual</div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Total</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualTotal)}</span>
          </div>
          
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Profits</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualProfits)}</span>
          </div>
          
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Costs</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualCosts)}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default ForecastActualCard;
